import React, { cloneElement, useState } from 'react';
import { Tooltip } from '@mui/material';
import type { ReactElement } from 'react';

interface CopyWithTooltipProps {
  value: string;
  children: ReactElement;
  tooltipTitle?: string;
  tooltipPlacement?: 'bottom' | 'top' | 'left' | 'right';
  duration?: number;
}

/**
 * Component that wraps a child element and adds copy-to-clipboard functionality
 * with an animated "Copied!" tooltip that appears for 2 seconds after clicking.
 *
 * @param {string} value - The text value to copy to clipboard
 * @param {ReactElement} children - The child element that will trigger the copy action
 * @param {string} tooltipTitle - Optional custom tooltip text (default: "Copied!")
 * @param {string} tooltipPlacement - Optional tooltip placement (default: "top")
 * @param {number} duration - Optional tooltip display duration in ms (default: 2000)
 * @returns {JSX.Element}
 */
const CopyWithTooltip: React.FC<CopyWithTooltipProps> = ({
  value,
  children,
  tooltipTitle = 'Copied!',
  tooltipPlacement = 'top',
  duration = 1000,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const handleCopy = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    navigator.clipboard.writeText(value);
    setTooltipOpen(true);

    setTimeout(() => {
      setTooltipOpen(false);
    }, duration);
  };

  // Clone the child element and add onClick handler
  const { title, ...restProps } = children.props;
  const childWithClick = cloneElement(children, {
    ...restProps,
    onClick: (e: React.MouseEvent) => {
      handleCopy(e);
      // Call original onClick if it exists
      if (children.props.onClick) {
        children.props.onClick(e);
      }
    },
  });

  return (
    <Tooltip
      open={tooltipOpen}
      title={tooltipTitle}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      placement={tooltipPlacement}
      arrow
    >
      <span style={{ display: 'inline-flex' }}>{childWithClick}</span>
    </Tooltip>
  );
};

export default CopyWithTooltip;
